import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import {
  Calculator,
  User,
  Users,
  Home,
  HomeIcon,
  Info,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Wallet,
  PiggyBank,
  Building
} from 'lucide-react';

const DebtToIncomeCalculator = () => {
  // State pentru venituri
  const [venitSolicitant, setVenitSolicitant] = useState('');
  const [includeBonuriMasa, setIncludeBonuriMasa] = useState(false);
  const [bonuriMasaSolicitant, setBonuriMasaSolicitant] = useState('');
  
  // State pentru codebitor
  const [areCodebitor, setAreCodebitor] = useState(false);
  const [venitCodebitor, setVenitCodebitor] = useState('');
  const [bonuriMasaCodebitor, setBonuriMasaCodebitor] = useState('');
  
  // State pentru situația imobiliară
  const [situatieImobiliara, setSituatieImobiliara] = useState('primul_imobil');
  
  // State pentru excepția de 50% (disponibilă o dată la 3 luni)
  const [exceptieDisponibila, setExceptieDisponibila] = useState(false);
  
  // State pentru credite existente
  const [rateExistente, setRateExistente] = useState('');

  // Calculează venitul total
  const venitTotalSolicitant = useMemo(() => {
    const venit = parseFloat(venitSolicitant) || 0;
    const bonuri = includeBonuriMasa ? (parseFloat(bonuriMasaSolicitant) || 0) : 0;
    return venit + bonuri;
  }, [venitSolicitant, includeBonuriMasa, bonuriMasaSolicitant]);

  const venitTotalCodebitor = useMemo(() => {
    if (!areCodebitor) return 0;
    const venit = parseFloat(venitCodebitor) || 0;
    const bonuri = includeBonuriMasa ? (parseFloat(bonuriMasaCodebitor) || 0) : 0;
    return venit + bonuri;
  }, [areCodebitor, venitCodebitor, includeBonuriMasa, bonuriMasaCodebitor]);

  const venitTotalCumulat = venitTotalSolicitant + venitTotalCodebitor;

  // Determină gradul de îndatorare aplicabil
  const calculGradIndatorare = useMemo(() => {
    let gradBaza = 0;
    let motiv = '';
    let exceptie50 = false;
    let motivExceptie = '';
    let indeplinesteConditiiExceptie = false;
    let conditieNeindeplinita = '';

    // Verifică dacă se îndeplinesc condițiile pentru excepția de 50%
    if (venitTotalSolicitant >= 6000) {
      indeplinesteConditiiExceptie = true;
      motivExceptie = 'Venit solicitant ≥ 6.000 lei NET (cu bonuri de masă incluse)';
    } else if (areCodebitor && venitTotalCumulat >= 8000) {
      indeplinesteConditiiExceptie = true;
      motivExceptie = 'Venit cumulat solicitant + codebitor ≥ 8.000 lei';
    } else {
      // Nu îndeplinește condițiile de venit
      if (areCodebitor) {
        conditieNeindeplinita = `Venit insuficient pentru excepție. Necesar: solicitant ≥ 6.000 lei sau cumulat ≥ 8.000 lei (actual: ${venitTotalCumulat.toLocaleString('ro-RO')} lei)`;
      } else {
        conditieNeindeplinita = `Venit insuficient pentru excepție. Necesar: ≥ 6.000 lei (actual: ${venitTotalSolicitant.toLocaleString('ro-RO')} lei)`;
      }
    }

    // Excepția se aplică DOAR dacă este disponibilă ȘI condițiile sunt îndeplinite
    if (exceptieDisponibila && indeplinesteConditiiExceptie) {
      exceptie50 = true;
    }

    // Determină gradul de bază în funcție de situația imobiliară
    if (situatieImobiliara === 'primul_imobil') {
      gradBaza = 45;
      motiv = 'Primul imobil achiziționat - solicitantul nu deține și nu a deținut anterior niciun imobil cu destinație locativă';
    } else {
      gradBaza = 40;
      motiv = 'Deține deja un imobil - proprietar integral sau parțial pe un imobil cu destinație locativă';
    }

    // Aplică excepția dacă e cazul
    const gradFinal = exceptie50 ? 50 : gradBaza;

    return {
      gradBaza,
      gradFinal,
      motiv,
      exceptie50,
      motivExceptie,
      indeplinesteConditiiExceptie,
      conditieNeindeplinita,
      exceptieDisponibila
    };
  }, [situatieImobiliara, venitTotalSolicitant, venitTotalCumulat, areCodebitor, exceptieDisponibila]);

  // Calculează rata maximă și disponibilul
  const rezultate = useMemo(() => {
    const rateExistenteVal = parseFloat(rateExistente) || 0;
    const gradProcent = calculGradIndatorare.gradFinal / 100;
    
    // Rata maximă totală permisă (toate creditele)
    const rataMaximaTotala = venitTotalCumulat * gradProcent;
    
    // Disponibil pentru credit nou
    const disponibilCreditNou = Math.max(0, rataMaximaTotala - rateExistenteVal);
    
    // Grad de îndatorare curent
    const gradCurent = venitTotalCumulat > 0 
      ? (rateExistenteVal / venitTotalCumulat) * 100 
      : 0;
    
    // Grad de îndatorare rămas disponibil
    const gradDisponibil = calculGradIndatorare.gradFinal - gradCurent;

    return {
      venitTotalCumulat,
      rataMaximaTotala,
      disponibilCreditNou,
      gradCurent,
      gradDisponibil,
      rateExistente: rateExistenteVal
    };
  }, [venitTotalCumulat, rateExistente, calculGradIndatorare]);

  // Format număr cu separator de mii
  const formatNumber = (num) => {
    return new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Reset form
  const handleReset = () => {
    setVenitSolicitant('');
    setBonuriMasaSolicitant('');
    setAreCodebitor(false);
    setVenitCodebitor('');
    setBonuriMasaCodebitor('');
    setSituatieImobiliara('primul_imobil');
    setRateExistente('');
    setIncludeBonuriMasa(false);
    setExceptieDisponibila(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
          <TrendingUp className="h-7 w-7 text-blue-600" />
          Calculator Grad de Îndatorare
        </h2>
        <p className="text-gray-600 mt-2">
          Calculează gradul maxim de îndatorare conform normelor BNR
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coloana stânga - Input-uri */}
        <div className="space-y-6">
          {/* Card Venit Solicitant */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Venit Solicitant Principal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="venitSolicitant">Venit NET lunar (lei)</Label>
                <Input
                  id="venitSolicitant"
                  type="number"
                  placeholder="Ex: 5000"
                  value={venitSolicitant}
                  onChange={(e) => setVenitSolicitant(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeBonuri"
                  checked={includeBonuriMasa}
                  onCheckedChange={setIncludeBonuriMasa}
                />
                <Label htmlFor="includeBonuri" className="cursor-pointer">
                  Include bonuri de masă / tichete
                </Label>
              </div>

              {includeBonuriMasa && (
                <div>
                  <Label htmlFor="bonuriMasaSolicitant">Valoare bonuri de masă (lei/lună)</Label>
                  <Input
                    id="bonuriMasaSolicitant"
                    type="number"
                    placeholder="Ex: 600"
                    value={bonuriMasaSolicitant}
                    onChange={(e) => setBonuriMasaSolicitant(e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}

              {venitTotalSolicitant > 0 && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Venit total solicitant:</strong> {formatNumber(venitTotalSolicitant)} lei
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card Codebitor */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Codebitor / Coplătitor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="areCodebitor"
                  checked={areCodebitor}
                  onCheckedChange={setAreCodebitor}
                />
                <Label htmlFor="areCodebitor" className="cursor-pointer">
                  Există codebitor / coplătitor
                </Label>
              </div>

              {areCodebitor && (
                <>
                  <div>
                    <Label htmlFor="venitCodebitor">Venit NET lunar codebitor (lei)</Label>
                    <Input
                      id="venitCodebitor"
                      type="number"
                      placeholder="Ex: 4000"
                      value={venitCodebitor}
                      onChange={(e) => setVenitCodebitor(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  {includeBonuriMasa && (
                    <div>
                      <Label htmlFor="bonuriMasaCodebitor">Valoare bonuri de masă codebitor (lei/lună)</Label>
                      <Input
                        id="bonuriMasaCodebitor"
                        type="number"
                        placeholder="Ex: 500"
                        value={bonuriMasaCodebitor}
                        onChange={(e) => setBonuriMasaCodebitor(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}

                  {venitTotalCodebitor > 0 && (
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-sm text-purple-800">
                        <strong>Venit total codebitor:</strong> {formatNumber(venitTotalCodebitor)} lei
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Card Situație Imobiliară */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Home className="h-5 w-5 text-green-600" />
                Situație Imobiliară
              </CardTitle>
              <CardDescription>
                Selectează situația care se aplică solicitantului
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={situatieImobiliara}
                onValueChange={setSituatieImobiliara}
                className="space-y-3"
              >
                <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem value="primul_imobil" id="primul_imobil" className="mt-1" />
                  <div>
                    <Label htmlFor="primul_imobil" className="cursor-pointer font-medium">
                      🏠 Primul imobil achiziționat
                    </Label>
                    <p className="text-sm text-gray-500 mt-1">
                      Nu deține și nu a deținut anterior niciun imobil cu destinație locativă
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem value="detine_imobil" id="detine_imobil" className="mt-1" />
                  <div>
                    <Label htmlFor="detine_imobil" className="cursor-pointer font-medium">
                      🏘️ Deține deja un imobil
                    </Label>
                    <p className="text-sm text-gray-500 mt-1">
                      Proprietar integral sau parțial pe un imobil locativ (achitat sau ipotecat)
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Card Excepție 50% */}
          <Card className={`border-2 ${exceptieDisponibila ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className={`h-5 w-5 ${exceptieDisponibila ? 'text-green-600' : 'text-gray-400'}`} />
                Excepție Grad 50%
              </CardTitle>
              <CardDescription>
                Disponibilă o dată la 3 luni (conform normelor BNR)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-3 p-3 border rounded-lg bg-white">
                <Checkbox
                  id="exceptieDisponibila"
                  checked={exceptieDisponibila}
                  onCheckedChange={setExceptieDisponibila}
                  className="mt-1"
                />
                <div>
                  <Label htmlFor="exceptieDisponibila" className="cursor-pointer font-medium">
                    Excepția de 50% este disponibilă acum
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Bifează doar dacă clientul poate beneficia de excepție în această perioadă
                  </p>
                </div>
              </div>

              {exceptieDisponibila && (
                <div className={`p-3 rounded-lg ${calculGradIndatorare.indeplinesteConditiiExceptie ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  {calculGradIndatorare.indeplinesteConditiiExceptie ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Condiții îndeplinite!</p>
                        <p className="text-sm text-green-700">{calculGradIndatorare.motivExceptie}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Condiții neîndeplinite</p>
                        <p className="text-sm text-yellow-700">{calculGradIndatorare.conditieNeindeplinita || 'Introduceți venitul pentru a verifica eligibilitatea.'}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                <strong>Condiții pentru excepția de 50%:</strong>
                <ul className="list-disc list-inside mt-1">
                  <li>Venit solicitant ≥ 6.000 lei NET (cu bonuri de masă)</li>
                  <li>SAU venit cumulat cu codebitor ≥ 8.000 lei</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Card Rate Existente */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <PiggyBank className="h-5 w-5 text-orange-600" />
                Obligații de Plată Existente
              </CardTitle>
              <CardDescription>
                Rate la credite existente (ipotecar, consum, card, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="rateExistente">Total rate lunare existente (lei)</Label>
                <Input
                  id="rateExistente"
                  type="number"
                  placeholder="Ex: 1500"
                  value={rateExistente}
                  onChange={(e) => setRateExistente(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Include toate ratele: credit ipotecar, credit consum, carduri de credit, leasing, etc.
                </p>
              </div>
            </CardContent>
          </Card>

          <Button onClick={handleReset} variant="outline" className="w-full">
            Resetează Calculatorul
          </Button>
        </div>

        {/* Coloana dreapta - Rezultate */}
        <div className="space-y-6">
          {/* Card Grad de Îndatorare Aplicabil */}
          <Card className={`border-2 ${calculGradIndatorare.exceptie50 ? 'border-green-500 bg-green-50' : 'border-blue-500 bg-blue-50'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Grad de Îndatorare Aplicabil
                </span>
                <Badge className={`text-xl px-4 py-1 ${calculGradIndatorare.exceptie50 ? 'bg-green-600' : 'bg-blue-600'}`}>
                  {calculGradIndatorare.gradFinal}%
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700">{calculGradIndatorare.motiv}</p>
              </div>

              {calculGradIndatorare.exceptie50 && (
                <div className="flex items-start gap-2 bg-green-100 p-3 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Excepție 50% aplicată!</p>
                    <p className="text-sm text-green-700">{calculGradIndatorare.motivExceptie}</p>
                  </div>
                </div>
              )}

              {!calculGradIndatorare.exceptie50 && !exceptieDisponibila && venitTotalSolicitant > 0 && (
                <div className="flex items-start gap-2 bg-gray-100 p-3 rounded-lg">
                  <Info className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700">
                      Excepția de 50% nu este activată. Activează din secțiunea "Excepție Grad 50%" dacă este disponibilă.
                    </p>
                  </div>
                </div>
              )}

              {!calculGradIndatorare.exceptie50 && exceptieDisponibila && !calculGradIndatorare.indeplinesteConditiiExceptie && venitTotalSolicitant > 0 && (
                <div className="flex items-start gap-2 bg-yellow-100 p-3 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-yellow-800">
                      Excepția de 50% este activată, dar condițiile de venit nu sunt îndeplinite.
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Necesar: venit solicitant ≥ 6.000 lei {areCodebitor && 'sau venit cumulat ≥ 8.000 lei'}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card Rezultate Calcul */}
          {venitTotalCumulat > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-blue-600" />
                  Rezultate Calcul
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Venit Total */}
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Venit NET total lunar:</span>
                  <span className="font-bold text-lg">{formatNumber(rezultate.venitTotalCumulat)} lei</span>
                </div>

                {/* Rata Maximă Totală */}
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <div>
                    <span className="text-blue-800">Rată maximă totală permisă:</span>
                    <p className="text-xs text-blue-600">({calculGradIndatorare.gradFinal}% din venit)</p>
                  </div>
                  <span className="font-bold text-lg text-blue-700">{formatNumber(rezultate.rataMaximaTotala)} lei</span>
                </div>

                {/* Rate Existente */}
                {rezultate.rateExistente > 0 && (
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <span className="text-orange-800">Rate existente:</span>
                    <span className="font-bold text-lg text-orange-700">- {formatNumber(rezultate.rateExistente)} lei</span>
                  </div>
                )}

                {/* Disponibil pentru Credit Nou */}
                <div className={`flex justify-between items-center p-4 rounded-lg ${rezultate.disponibilCreditNou > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <div>
                    <span className={`font-medium ${rezultate.disponibilCreditNou > 0 ? 'text-green-800' : 'text-red-800'}`}>
                      Disponibil pentru credit nou:
                    </span>
                    <p className="text-xs text-gray-600">Rata maximă pe care o poți lua</p>
                  </div>
                  <span className={`font-bold text-2xl ${rezultate.disponibilCreditNou > 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatNumber(rezultate.disponibilCreditNou)} lei
                  </span>
                </div>

                {/* Grad Curent și Disponibil */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Grad curent</p>
                    <p className="text-xl font-bold text-gray-700">{rezultate.gradCurent.toFixed(1)}%</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Disponibil rămas</p>
                    <p className={`text-xl font-bold ${rezultate.gradDisponibil > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {rezultate.gradDisponibil.toFixed(1)}%
                    </p>
                  </div>
                </div>

                {/* Bară de progres vizuală */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>0%</span>
                    <span>Grad îndatorare</span>
                    <span>{calculGradIndatorare.gradFinal}%</span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${
                        rezultate.gradCurent <= calculGradIndatorare.gradFinal * 0.7 
                          ? 'bg-green-500' 
                          : rezultate.gradCurent <= calculGradIndatorare.gradFinal 
                            ? 'bg-yellow-500' 
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(100, (rezultate.gradCurent / calculGradIndatorare.gradFinal) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-center text-gray-500 mt-1">
                    Utilizare curentă: {((rezultate.gradCurent / calculGradIndatorare.gradFinal) * 100).toFixed(0)}%
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card Informații Legale */}
          <Card className="border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
                <Info className="h-4 w-4" />
                Informații Grad de Îndatorare
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-gray-500 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 bg-blue-50 rounded text-center">
                  <p className="font-bold text-blue-700">40%</p>
                  <p>Deține imobil</p>
                </div>
                <div className="p-2 bg-green-50 rounded text-center">
                  <p className="font-bold text-green-700">45%</p>
                  <p>Primul imobil</p>
                </div>
                <div className="p-2 bg-purple-50 rounded text-center">
                  <p className="font-bold text-purple-700">50%</p>
                  <p>Excepție venit</p>
                </div>
              </div>
              <p className="mt-2">
                <strong>Excepție 50%:</strong> Venit solicitant ≥ 6.000 lei NET (cu bonuri de masă) 
                SAU venit cumulat cu codebitor ≥ 8.000 lei.
              </p>
              <p>
                <strong>Notă:</strong> Calculele sunt orientative. Băncile pot aplica criterii suplimentare.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DebtToIncomeCalculator;
